import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { formatDateOrEmpty } from '@helpers/dates';
import { styled } from '@mui/material/styles';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { Typography } from '@components/Typography';
import MarkdownRenderer, { isMarkdownContent } from '@components/MarkdownRenderer';
import { MessageType } from '@constants/inbox';
import { getFullName } from '@helpers/userName';
import { renderTextWithLinks } from '@helpers/parse-link';
import TypingIndicator from './TypeIndicator';

const Container = styled('div', {
    shouldForwardProp: prop => prop !== 'type',
})(({ type }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 4,
    maxWidth: 300,
    alignItems: type === MessageType.Outgoing ? 'flex-end' : 'flex-start',
    alignSelf: type === MessageType.Outgoing ? 'flex-end' : 'flex-start',
    '@media (max-width: 768px)': {},
}));

const MessageWrapper = styled('div', {
    shouldForwardProp: prop => prop !== 'type' && prop !== 'multiple' && prop !== 'isLast',
})(({ type, multiple, isLast }) => ({
    padding: 12,
    width: 'fit-content',
    backgroundColor: type === MessageType.Outgoing ? 'var(--primary-main)' : '#fff',
    borderRadius: type === MessageType.Outgoing ? '8px 8px 0px 8px' : '8px 8px 8px 0px',
    color: type === MessageType.Outgoing ? '#fff' : 'var(--text-primary)',
    '& svg > path': {
        fill: 'var(--text-primary)',
    },
    ...(multiple && {
        borderRadius: '8px',
        '&:not(:last-child)': {
            marginBottom: 4,
        },
    }),
    ...(multiple &&
        isLast && {
            borderRadius: type === MessageType.Outgoing ? '8px 8px 0 8px' : '8px 8px 8px 0',
        }),
}));

const StyledMarkdownRenderer = styled(MarkdownRenderer)({
    maxWidth: 'calc(300px - 24px)',
});

const ReadIcon = styled(DoneAllIcon)(() => ({
    marginLeft: '4px',
    maxWidth: '12px',
    maxHeight: '14px',
    color: 'var(--primary-main)',
}));

const Message = ({ data, currentUser }) => {
    const isOutgoing = data.type === MessageType.Outgoing;
    const language = useSelector(state => state.auth.user?.language);

    const currentUserName = useMemo(() => getFullName(currentUser?.firstName, currentUser?.lastName), [currentUser]);
    const isTyping = data.id === 'TYPING';

    return (
        <Container type={data.type}>
            {isTyping ? null : (
                <Typography
                    variant="caption"
                    sx={{
                        color: 'var(--text-secondary)',
                        textAlign: isOutgoing ? 'right' : 'left',
                        marginBottom: '4px',
                    }}
                >
                    {isOutgoing ? currentUserName : 'Pulse'}
                </Typography>
            )}
            {data.children.length === 1 ? (
                isTyping ? (
                    <TypingIndicator />
                ) : (
                    <MessageWrapper type={data.type} isLast>
                        {isMarkdownContent(data.children[0].message) ? (
                            <StyledMarkdownRenderer content={data.children[0].message} />
                        ) : (
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {renderTextWithLinks(data.children[0].message)}
                            </Typography>
                        )}
                    </MessageWrapper>
                )
            ) : (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isOutgoing ? 'flex-end' : 'flex-start',
                    }}
                >
                    {data.children.map((item, index) => (
                        <MessageWrapper
                            key={index}
                            type={data.type}
                            isLast={index === data.children.length - 1}
                            multiple
                        >
                            {item.id !== 'TYPING' && isMarkdownContent(item.message) ? (
                                <StyledMarkdownRenderer content={item.message} />
                            ) : (
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {item.id === 'TYPING' ? <TypingIndicator /> : item.message}
                                </Typography>
                            )}
                        </MessageWrapper>
                    ))}
                </div>
            )}
            {!isTyping ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: isOutgoing ? 'flex-end' : 'flex-start',
                        alignItems: 'center',
                        marginTop: 4,
                    }}
                >
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                        {formatDateOrEmpty(data.createdAt, 'd MMM • HH:mm', language)}
                    </Typography>
                    {isOutgoing && <ReadIcon />}
                </div>
            ) : null}
        </Container>
    );
};

export default Message;
